import React, { useRef, useEffect } from 'react';
import { Table, Badge as RBBadge, Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { useTaskDetails } from 'providers/TaskDetailsProvider';
import { formatDate } from 'helpers/utils';
import { TaskResponse } from 'services/api/task';
import TaskModal from 'pages/apps/tasks/components/TaskModal';
import { JobTasksProps } from './types/types';

const JobTasks: React.FC<JobTasksProps> = ({
  isLoadingTasks,
  jobTasks,
  scrollToTasks,
  setScrollToTasks,
  showTaskModal,
  setShowTaskModal,
  employees,
  jobs,
  onTaskModalClose
}) => {
  const { openTask } = useTaskDetails();
  const tasksSectionRef = useRef<HTMLDivElement>(null);

  const handleOpenTaskModal = () => {
    setShowTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    onTaskModalClose();
  };

  const handleTaskClick = (taskId: string) => openTask(taskId);

  useEffect(() => {
    if (scrollToTasks && tasksSectionRef.current) {
      setTimeout(() => {
        tasksSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        setScrollToTasks?.(false);
      }, 300);
    }
  }, [scrollToTasks, setScrollToTasks]);

  return (
    <>
      <div ref={tasksSectionRef} className="mb-7">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Tasks</h4>
          <Button
            variant="primary"
            size="sm"
            onClick={handleOpenTaskModal}
            className="px-3"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add Task
          </Button>
        </div>
        {isLoadingTasks ? (
          <Spinner animation="border" variant="primary" />
        ) : jobTasks && jobTasks.length > 0 ? (
          <Table responsive>
            <thead>
              <tr className="text-nowrap">
                <th>Task</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Due Date</th>
                <th>Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {jobTasks.map((task: TaskResponse) => (
                <tr
                  key={task.id || task._id}
                  onClick={() => handleTaskClick(task.id || task._id)}
                  className="cursor-pointer"
                >
                  <td>
                    <div className="d-flex flex-column">
                      <span className="fw-medium">{task.task}</span>
                      <small className="text-muted">
                        {task.description && task.description.length > 50
                          ? `${task.description.substring(0, 50)}...`
                          : task.description}
                      </small>
                    </div>
                  </td>
                  <td>
                    <RBBadge
                      bg={
                        task.completed
                          ? 'success'
                          : task.status === 'in progress'
                          ? 'primary'
                          : 'warning'
                      }
                    >
                      {task.completed ? 'Completed' : task.status}
                    </RBBadge>
                  </td>
                  <td>
                    <RBBadge
                      bg={
                        task.priority === 'high'
                          ? 'danger'
                          : task.priority === 'medium'
                          ? 'warning'
                          : 'info'
                      }
                    >
                      {task.priority}
                    </RBBadge>
                  </td>
                  <td>{task.date ? formatDate(task.date) : '—'}</td>
                  <td>
                    {task.assignedTo.map(assignee => (
                      <RBBadge
                        key={assignee.id || assignee._id}
                        bg="primary"
                        className="me-1 mb-1"
                      >
                        {assignee.firstName || ''} {assignee.lastName || ''}
                      </RBBadge>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div
            className="text-center py-4 border rounded cursor-pointer"
            onClick={handleOpenTaskModal}
          >
            <FontAwesomeIcon
              icon={faListCheck}
              className="mb-3 fs-4 text-300 task-icon"
            />
            <p className="mb-2">No tasks associated with this job</p>
          </div>
        )}
      </div>

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          show={showTaskModal}
          handleClose={handleCloseTaskModal}
          employees={employees || []}
          jobs={jobs || []}
        />
      )}
    </>
  );
};

export default JobTasks;
